import {
  FC,
  ReactNode,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import {
  Box,
  CircularProgress,
  createStyles,
  Hidden,
  makeStyles,
  Paper,
  Typography,
  useMediaQuery,
} from "@material-ui/core";
import {
  List as ListIcon,
  ViewModuleOutlined as ViewModuleOutlinedIcon,
} from "@material-ui/icons";
import { ToggleButton, ToggleButtonGroup } from "@material-ui/lab";
import { isEqual } from "lodash";
import { usePostHog } from "posthog-js/react";
import { useLocation } from "react-router";

import {
  FilterListButton,
  ListingsGrid,
  ListingTable,
  Modal,
  OrderBookPagesHeader,
  Pagination,
  ResetButton,
  SearchField,
  UseQueryParamsControls,
} from "components";
import { TTableColumn } from "components/ListingTable/types";
import { OrderBookPagesHeaderProps } from "components/OrderBookPagesHeader";
import { BaseContainer } from "containers";
import { AppContext } from "contexts";
import { usePrevious } from "hooks";
import useQueryParams from "hooks/useQueryParams";
import {
  UiParam,
  UiParams,
  ValidDynamicRestUrlParams,
} from "hooks/useQueryParams/types";
import { makeQueryString } from "lib/dynamicRest";
import { longDebounceTime } from "services/constants";
import {
  useInventoryListings,
  useInventoryListingsPaginated,
} from "services/inventory";
import { InventoryListing } from "services/inventory/types";
import { EXCHANGE_SEARCH } from "services/postHog/constants";
import theme from "theme";

const useStyles = makeStyles(() =>
  createStyles({
    filterSortSectionTitle: {
      fontSize: "2rem",
      fontWeight: theme.typography.fontWeightBold,
    },
    paper: {
      borderRadius: theme.custom.shape.borderRadiusSoft,
    },
    toggleButtonGroup: {
      borderRadius: theme.custom.shape.borderRadiusSoft,
      "& .MuiToggleButton-root": {
        borderRadius: theme.custom.shape.borderRadiusSoft,
      },
      "& .MuiToggleButtonGroup-groupedHorizontal:not(:last-child)": {
        borderTopRightRadius: 0,
        borderBottomRightRadius: 0,
      },
      "& .MuiToggleButtonGroup-groupedHorizontal:not(:first-child)": {
        borderTopLeftRadius: 0,
        borderBottomLeftRadius: 0,
      },
      "& .Mui-selected": {
        backgroundColor: theme.custom.palette.purple.main,
        color: theme.custom.palette.gray.light,
      },
      "& .MuiToggleButton-root:hover": {
        backgroundColor: theme.custom.palette.purple.light,
      },
    },
    toggleButton: {
      height: "45px",
    },
  }),
);

export interface FilterConfig {
  userId: number | undefined;
  uiParams: UiParams;
  setUiParams: (setState: (uiParams: UiParams) => UiParams) => void;
  hideResetButton: boolean;
  isDefaultUiParams: boolean;
  handleResetFilters: () => void;
}
interface BaseListingsProps {
  buildFilters: (filterConfig: FilterConfig) => JSX.Element[];
  makeDefaultUiParams: (
    userId: number | undefined,
    isListView: boolean,
  ) => UiParams;
  validDynamicRestUrlParams: ValidDynamicRestUrlParams;
  validUiSortOptions: UiParam[];
  desktopColumns: TTableColumn<InventoryListing>[];
  mobileColumns: TTableColumn<InventoryListing>[];
  headerContent: OrderBookPagesHeaderProps;
  title: string;
  baseUrl: string;
  hideUserNotificationTags?: string[];
  showAcceptingFinalOffersSection?: boolean;
}

type ResponsiveHiddenProps = {
  children: ReactNode;
  xsDown?: boolean;
  mdDown?: boolean;
  lgUp?: boolean;
};

// Fix this when we upgrade to MUI v5 in RXT-2628
const ResponsiveHidden: FC<ResponsiveHiddenProps> = props => {
  return <Hidden implementation="js" {...props} />;
};

const BaseListings: FC<BaseListingsProps> = ({
  buildFilters,
  makeDefaultUiParams,
  validDynamicRestUrlParams,
  validUiSortOptions,
  desktopColumns,
  mobileColumns,
  headerContent,
  title,
  baseUrl,
  hideUserNotificationTags,
  showAcceptingFinalOffersSection,
}) => {
  const classes = useStyles();
  const posthog = usePostHog();

  const { user } = useContext(AppContext);
  const userId = user?.userId;

  const isDownXs = useMediaQuery(theme.breakpoints.down("xs"), { noSsr: true });
  const isDesktop = useMediaQuery(theme.breakpoints.up("md"), { noSsr: true });
  const previousIsDownXs = usePrevious(isDownXs);

  const [totalPages, setTotalPages] = useState(0);

  const location = useLocation();
  const urlSearchParams = new URLSearchParams(location.search);
  const isListView = urlSearchParams.get("page_size") === "50";
  const defaultUiParams = useMemo(
    () => makeDefaultUiParams(user?.userId, isListView),
    [makeDefaultUiParams, user?.userId, isListView],
  );

  const [uiParams, setUiParams, queryOptions] = useQueryParams(
    validDynamicRestUrlParams,
    validUiSortOptions,
    defaultUiParams,
  );

  // Modified to ignore pageSize when comparing, preventing reset button flickering during grid/list toggle
  const isDefaultUiParams = useMemo(() => {
    // Create copies of the params objects without the pageSize property for comparison
    const uiParamsWithoutPageSize = { ...uiParams };
    const defaultUiParamsWithoutPageSize = { ...defaultUiParams };

    if (uiParamsWithoutPageSize.pageSize) {
      delete uiParamsWithoutPageSize.pageSize;
    }
    if (defaultUiParamsWithoutPageSize.pageSize) {
      delete defaultUiParamsWithoutPageSize.pageSize;
    }

    return isEqual(defaultUiParamsWithoutPageSize, uiParamsWithoutPageSize);
  }, [uiParams, defaultUiParams]);

  const [isFilterSortModalOpen, setIsFilterSortModalOpen] = useState(false);

  const handleResetFilters = useCallback(() => {
    // Preserve the current pageSize value (grid/list view preference) when resetting filters
    setUiParams(() => ({
      ...defaultUiParams,
      pageSize: uiParams.pageSize, // Keep the current grid/list view setting
    }));
  }, [defaultUiParams, setUiParams, uiParams.pageSize]);

  const handleSearchChange = useCallback(
    (query: string) => {
      setUiParams(prev => ({
        ...prev,
        page: { ...prev.page, value: "1" },
        search: {
          ...prev.search,
          value: query,
        },
      }));
    },
    [setUiParams],
  );

  const mdDownFilters = buildFilters({
    userId,
    uiParams,
    setUiParams,
    hideResetButton: true,
    isDefaultUiParams,
    handleResetFilters,
  });

  const lgUpFilters = buildFilters({
    userId,
    uiParams,
    setUiParams,
    hideResetButton: false,
    isDefaultUiParams,
    handleResetFilters,
  });

  useEffect(() => {
    if (isDownXs && !previousIsDownXs) {
      setUiParams(prev => ({
        ...prev,
        page: { ...prev.page, value: "1" },
        pageSize: { ...prev.pageSize, value: "15" },
      }));
    }
  }, [previousIsDownXs, isDownXs, setUiParams]);

  const acceptingFinalOffersQueries = useInventoryListings({
    filter: {
      "state.in": ["accepting_final_offers"],
    },
    sort: ["accepting_final_offers_start_at"],
  });

  const acceptingFinalOffersListings = acceptingFinalOffersQueries.data ?? [];

  const listingsQueries = useInventoryListingsPaginated(queryOptions, data => {
    if (queryOptions.search.length > 2) {
      const resultCount = data.pages[0].meta.total_results;
      posthog?.capture(EXCHANGE_SEARCH, {
        actionType: "search",
        searchQuery: queryOptions.search,
        resultCount,
      });
    }
  });
  const [listingsQueriesPages, listingsQueriesMeta] = useMemo(() => {
    const listingsQueriesMeta =
      listingsQueries?.data?.pages && listingsQueries?.data?.pages[0].meta;
    return [listingsQueries?.data?.pages, listingsQueriesMeta];
  }, [listingsQueries?.data?.pages]);

  useEffect(() => {
    if (!!listingsQueriesPages) {
      setTotalPages(listingsQueriesMeta.total_pages);
    }
  }, [listingsQueriesPages, listingsQueriesMeta]);

  const listings =
    (listingsQueriesPages && listingsQueriesPages[0].results) ?? [];

  const foundListingsMessage = listingsQueries.isLoading
    ? ""
    : listingsQueriesMeta?.total_results
    ? `Found ${listingsQueriesMeta.total_results} listings`
    : "No listings match your current search";

  const scrollTopRef = useRef<HTMLDivElement>(null);
  const handlePageChange = useCallback(
    (_event: any, value: number) => {
      window.scrollTo(0, scrollTopRef.current.offsetTop);
      setUiParams(prev => ({
        ...prev,
        page: { ...prev.page, value: String(value) },
      }));
    },
    [setUiParams],
  );

  const handleToggleListView = () => {
    const isChangingToListView = !isListView;

    posthog?.capture("Exchange grid/list", {
      actionType: isChangingToListView ? "list" : "grid",
    });

    setUiParams(prev => ({
      ...prev,
      page: { ...prev.page, value: "1" },
      pageSize: {
        ...prev.pageSize,
        value: isChangingToListView ? "50" : "15",
      },
    }));
  };

  const columns = useMemo(() => (isDesktop ? desktopColumns : mobileColumns), [
    isDesktop,
    desktopColumns,
    mobileColumns,
  ]);

  return (
    <BaseContainer
      renderFullWidthContent={() => (
        <OrderBookPagesHeader
          h1Content={headerContent.h1Content}
          subtitle1Content={headerContent.subtitle1Content}
          headerButtonContent={headerContent.headerButtonContent}
        />
      )}
    >
      {isFilterSortModalOpen && (
        <Modal open onClose={() => setIsFilterSortModalOpen(false)}>
          <Box marginTop={-6}>
            <Typography
              variant="h2"
              gutterBottom
              className={classes.filterSortSectionTitle}
            >
              Sort
            </Typography>
            <UseQueryParamsControls.SortControls
              uiParams={uiParams}
              setUiParams={setUiParams}
              isAuthenticated={!!userId}
              validUiSortOptions={validUiSortOptions}
            />
          </Box>
          <Box marginTop={4}>
            <Typography
              variant="h2"
              gutterBottom
              className={classes.filterSortSectionTitle}
            >
              Filter
            </Typography>
            <UseQueryParamsControls.FilterControls filters={mdDownFilters} />
          </Box>
          <Box
            width="100%"
            display="flex"
            justifyContent="center"
            marginBottom={3}
          >
            <ResetButton
              disabled={isDefaultUiParams}
              handleReset={handleResetFilters}
            />
          </Box>
        </Modal>
      )}
      <div ref={scrollTopRef} />
      <Box mb={4} mt={5}>
        <Typography variant="h1">{title}</Typography>
      </Box>
      <Box display="flex">
        <ResponsiveHidden mdDown>
          <Box minWidth="250px" width="250px" marginRight={1}>
            <UseQueryParamsControls.FilterControls filters={lgUpFilters} />
          </Box>
        </ResponsiveHidden>
        <Box width="100%">
          <Box mb={4} display="flex">
            <SearchField
              activeQuery={String(uiParams.search.value)}
              debounceTime={longDebounceTime}
              fieldId="search-listings"
              onChangeCommitted={handleSearchChange}
              placeholder="Search by ID, Title, Tag, Genre"
            />
            <ResponsiveHidden mdDown>
              <Box ml={2}>
                <UseQueryParamsControls.SortControls
                  uiParams={uiParams}
                  setUiParams={setUiParams}
                  isAuthenticated={!!userId}
                  validUiSortOptions={validUiSortOptions}
                />
              </Box>
            </ResponsiveHidden>
          </Box>
          <ResponsiveHidden lgUp>
            <Box
              minHeight={theme.spacing(4)}
              mb={2}
              width="100%"
              display="flex"
              justifyContent="space-between"
            >
              <FilterListButton
                label="Filter/Sort"
                onClick={() => setIsFilterSortModalOpen(true)}
              />
              <ResetButton
                disabled={isDefaultUiParams}
                handleReset={handleResetFilters}
              />
            </Box>
          </ResponsiveHidden>
          {showAcceptingFinalOffersSection &&
            !listingsQueries.isLoading &&
            !acceptingFinalOffersQueries.isLoading &&
            !!acceptingFinalOffersListings.length &&
            Number(uiParams.page.value) === 1 && (
              <Box mb={5}>
                <Box
                  minHeight={theme.spacing(4)}
                  mb={2}
                  width="100%"
                  display="flex"
                  justifyContent="space-between"
                >
                  <Typography variant="h6">Ending Soon</Typography>
                </Box>
                {acceptingFinalOffersQueries.data && (
                  <Box>
                    <ListingsGrid
                      listings={acceptingFinalOffersListings}
                      prevUrl={`${baseUrl}?${makeQueryString(queryOptions)}`}
                      hideUserNotificationTags={hideUserNotificationTags}
                      userId={user?.userId}
                    />
                  </Box>
                )}
              </Box>
            )}
          <Box
            minHeight={theme.spacing(4)}
            mb={2}
            width="100%"
            display="flex"
            justifyContent="space-between"
          >
            <Typography variant="h6">{foundListingsMessage}</Typography>
            <ResponsiveHidden xsDown>
              <div>
                <Paper elevation={3} className={classes.paper}>
                  <ToggleButtonGroup
                    className={classes.toggleButtonGroup}
                    value={isListView}
                    exclusive
                    size="small"
                    aria-label="grid or list view"
                    onChange={handleToggleListView}
                  >
                    <ToggleButton
                      value={false}
                      aria-label="grid"
                      className={classes.toggleButton}
                      disabled={!isListView}
                    >
                      <ViewModuleOutlinedIcon />
                    </ToggleButton>
                    <ToggleButton
                      value
                      aria-label="list"
                      className={classes.toggleButton}
                      disabled={isListView}
                    >
                      <ListIcon />
                    </ToggleButton>
                  </ToggleButtonGroup>
                </Paper>
              </div>
            </ResponsiveHidden>
          </Box>
          {listingsQueries.isLoading && (
            <Box display="flex" justifyContent="center" width="100%">
              <CircularProgress size={60} />
            </Box>
          )}
          {!listingsQueries.isLoading && !!listings.length && (
            <>
              {isListView ? (
                <ListingTable
                  columns={columns}
                  activeSortName={uiParams.sort.name}
                  listingsQueryOptions={queryOptions}
                  prevUrl={`${baseUrl}?${makeQueryString(queryOptions)}`}
                />
              ) : (
                <ListingsGrid
                  listings={listings}
                  prevUrl={`${baseUrl}?${makeQueryString(queryOptions)}`}
                  hideUserNotificationTags={hideUserNotificationTags}
                  userId={user?.userId}
                />
              )}
              <Pagination
                currentPage={Number(uiParams.page.value)}
                totalPages={totalPages}
                onChange={handlePageChange}
              />
            </>
          )}
        </Box>
      </Box>
    </BaseContainer>
  );
};

export default BaseListings;
